import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import PaymentReceiptPreview from '../components/PaymentReceiptPreview';
import BillViewModal from '../components/BillViewModal';
import { BILL_TYPES } from '../constants/billingTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface Patient {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { client_name: string; contact_number_1: string; contact_number_2?: string; email: string }[];
}

interface BillSummary {
  id: number;
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  status: string;
  bill_type: string | null;
  created_at: string;
  receipt_pdf_url: string | null;
  has_payment_receipt: boolean;
}

const STATUS_OPTIONS = ['Pending', 'Collected', 'Processed'];

const BILL_TYPE_META: Record<string, { label: string; bg: string; color: string; border: string }> = {
  nursing:           { label: 'Nursing',     bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  nursing_visit:     { label: 'Nursing Visit', bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
  doctor_visit:      { label: 'Doctor Visit', bg: '#f0fdfa', color: '#0f766e', border: '#99f6e4' },
  physio:            { label: 'Physio',      bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  medical_equipment: { label: 'Equipment',     bg: '#faf5ff', color: '#7e22ce', border: '#e9d5ff' },
  equipment_rental:  { label: 'Equip. Rental', bg: '#faf5ff', color: '#7e22ce', border: '#e9d5ff' },
  equipment_sale:    { label: 'Equip. Sale',   bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
  misc:              { label: 'Misc',        bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  consumables:       { label: 'Consumables', bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
  consumable_return: { label: 'Return',      bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const billTypeBadge = (type: string | null): React.CSSProperties => {
  const m = type ? BILL_TYPE_META[type] : null;
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: '9999px',
    fontSize: '0.75rem', fontWeight: 600,
    backgroundColor: m?.bg ?? '#f1f5f9',
    color: m?.color ?? '#64748b',
    border: `1px solid ${m?.border ?? '#e2e8f0'}`,
    whiteSpace: 'nowrap' as const,
  };
};


const statusStyle = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string }> = {
    Pending:   { bg: '#fef3c7', color: '#92400e' },
    Collected: { bg: '#dcfce7', color: '#15803d' },
    Processed: { bg: '#dbeafe', color: '#1e40af' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#475569' };
  return {
    display: 'inline-block', padding: '3px 10px', borderRadius: '9999px',
    fontSize: '0.78rem', fontWeight: 600, backgroundColor: s.bg, color: s.color,
  };
};

const fmt = (dateStr: string) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const fmtAmount = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PatientBillingHistory: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Screens that send us here (payment collections, for instance) pass the path
  // they came from so the back button returns there instead of dumping the user
  // on the billing client list. Anything that doesn't pass it falls back.
  const backTo = (location.state as { from?: string } | null)?.from ?? '/billing';
  const [patient, setPatient] = useState<Patient | null>(null);
  const [bills, setBills] = useState<BillSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [receiptPreviewBill, setReceiptPreviewBill] = useState<BillSummary | null>(null);
  const [viewBillId, setViewBillId] = useState<number | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/patients/${patientId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/billing/patients/${patientId}/bills`, { headers }),
      ]);
      setPatient(pRes.data);
      setBills(bRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (billId: number, newStatus: string) => {
    setUpdatingStatus(billId);
    setStatusDropdownOpen(null);
    try {
      await axios.patch(
        `${API_BASE_URL}/api/billing/bills/${billId}`,
        { status: newStatus },
        { headers },
      );
      setBills((prev) => prev.map((b) => b.id === billId ? { ...b, status: newStatus } : b));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (billId: number, label: string) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/billing/bills/${billId}`, { headers });
      setBills((prev) => prev.filter((b) => b.id !== billId));
      toast.success('Bill deleted');
    } catch {
      toast.error('Failed to delete bill');
    }
  };

  const totalPending = bills.filter((b) => b.status === 'Pending').reduce((s, b) => s + b.total_amount, 0);
  const totalCollected = bills.filter((b) => b.status === 'Collected').reduce((s, b) => s + b.total_amount, 0);

  const client = patient?.patient_clients?.[0];

  const statBox = (color: string): React.CSSProperties => ({
    flex: 1, padding: '0.75rem 1rem', borderRadius: '8px',
    backgroundColor: color === 'amber' ? '#fef3c7' : color === 'green' ? '#dcfce7' : '#f1f5f9',
    border: `1px solid ${color === 'amber' ? '#fcd34d' : color === 'green' ? '#86efac' : '#e2e8f0'}`,
  });

  const statValue = (color: string): React.CSSProperties => ({
    fontSize: '1.1rem', fontWeight: 700, marginTop: '2px',
    color: color === 'amber' ? '#92400e' : color === 'green' ? '#15803d' : '#334155',
  });

  const actionBtn = (color: string): React.CSSProperties => ({
    padding: '0.35rem 0.75rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 500,
    backgroundColor: color === 'blue' ? '#dbeafe' : color === 'red' ? '#fee2e2' : '#f1f5f9',
    color: color === 'blue' ? '#1e40af' : color === 'red' ? '#991b1b' : '#334155',
  });

  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    backBtn: {
      display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '1.5rem',
      color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem',
    },
    patientCard: {
      backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
      padding: '1.5rem', marginBottom: '1.5rem',
    },
    patientHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    patientName: { margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' },
    cid: { fontSize: '0.85rem', color: '#64748b', fontFamily: 'monospace', marginTop: '4px' },
    infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginTop: '1rem' },
    infoItem: {},
    infoLabel: { fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' },
    infoValue: { fontSize: '0.88rem', color: '#334155', marginTop: '2px' },
    statsRow: { display: 'flex', gap: '1rem' },
    statLabel: { fontSize: '0.75rem', color: '#64748b', fontWeight: 600 },
    billsCard: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    billsHeader: {
      padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    newBillBtn: {
      padding: '0.55rem 1.2rem', backgroundColor: '#2563eb', color: 'white',
      border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
    },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: {
      padding: '0.75rem 1rem', textAlign: 'left' as const, fontSize: '0.72rem',
      fontWeight: 600, color: '#64748b', textTransform: 'uppercase' as const,
      letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
    },
    td: { padding: '0.9rem 1rem', fontSize: '0.88rem', color: '#334155', borderBottom: '1px solid #f1f5f9' },
    emptyState: { textAlign: 'center' as const, padding: '3rem', color: '#94a3b8' },
  };

  if (loading) return <div style={styles.page}><div style={styles.emptyState}>Loading...</div></div>;
  if (!patient) return <div style={styles.page}><div style={styles.emptyState}>Patient not found.</div></div>;

  return (
    <div style={styles.page} onClick={() => setStatusDropdownOpen(null)}>
      <button style={styles.backBtn} onClick={() => navigate(backTo)}>
        ← Back{backTo === '/billing' ? ' to Billing' : ''}
      </button>

      {/* Patient Info Card */}
      <div style={styles.patientCard}>
        <div style={styles.patientHeader}>
          <div>
            <h2 style={styles.patientName}>{patient.patient_name}</h2>
            <div style={styles.cid}>CID: LHHS{patient.id}</div>
          </div>
          <div style={styles.statsRow}>
            <div style={statBox('amber')}>
              <div style={styles.statLabel}>Pending</div>
              <div style={statValue('amber')}>₹{fmtAmount(totalPending)}</div>
            </div>
            <div style={statBox('green')}>
              <div style={styles.statLabel}>Collected</div>
              <div style={statValue('green')}>₹{fmtAmount(totalCollected)}</div>
            </div>
            <div style={statBox('default')}>
              <div style={styles.statLabel}>Total Bills</div>
              <div style={statValue('default')}>{bills.length}</div>
            </div>
          </div>
        </div>

        <div style={styles.infoGrid}>
          {[
            { label: 'Client / Guardian', value: client?.client_name || '—' },
            { label: 'Contact', value: client?.contact_number_1 || '—' },
            { label: 'Hospital', value: patient.treating_details?.[0]?.hospital || patient.treating_hospital || '—' },
            { label: 'Doctor', value: patient.treating_details?.[0]?.doctor || patient.treating_doctor || '—' },
            { label: 'Sex', value: patient.sex || '—' },
            { label: 'Age', value: patient.age || '—' },
            { label: 'Case Start', value: fmt(patient.case_start_date) },
            { label: 'Email', value: client?.email || '—' },
          ].map(({ label, value }) => (
            <div key={label} style={styles.infoItem}>
              <div style={styles.infoLabel}>{label}</div>
              <div style={styles.infoValue}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bills Table */}
      <div style={styles.billsCard}>
        <div style={styles.billsHeader}>
          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>
            Invoice History
          </span>
          <button style={styles.newBillBtn} onClick={() => setShowTypePicker(true)}>
            + Create New Bill
          </button>
        </div>

        {bills.length === 0 ? (
          <div style={styles.emptyState}>
            No bills yet. Click "Create New Bill" to generate the first invoice.
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Bill No', 'Type', 'Date', 'Invoice Period', 'Amount', 'Status', 'Actions'].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bills.map((bill) => (
                <tr
                  key={bill.id}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {bill.bill_type === 'consumable_return'
                      ? bill.invoice_no || `R-${bill.bill_no}`
                      : bill.invoice_no || `#${bill.bill_no}`}
                  </td>
                  <td style={styles.td}>
                    <span style={billTypeBadge(bill.bill_type)}>
                      {bill.bill_type ? (BILL_TYPE_META[bill.bill_type]?.label ?? bill.bill_type) : '—'}
                    </span>
                  </td>
                  <td style={styles.td}>{fmt(bill.bill_date)}</td>
                  <td style={styles.td}>
                    {fmt(bill.invoice_period_start)} — {fmt(bill.invoice_period_end)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    ₹{fmtAmount(bill.total_amount)}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'inline-block' }}>
                      <span
                        style={{ ...statusStyle(bill.status), cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setDropdownPos({ top: rect.bottom + 4, left: rect.left });
                          setStatusDropdownOpen(statusDropdownOpen === bill.id ? null : bill.id);
                        }}
                      >
                        {updatingStatus === bill.id ? '...' : bill.status} ▾
                      </span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        style={actionBtn('default')}
                        onClick={() => setViewBillId(bill.id)}
                      >
                        View
                      </button>
                      <button
                        style={actionBtn('blue')}
                        onClick={() =>
                          bill.bill_type === 'consumables'
                            ? navigate(`/billing/consumables/edit/${bill.id}`)
                            : bill.bill_type === 'consumable_return'
                            ? navigate(`/billing/consumables/return/edit/${bill.id}`)
                            : navigate(`/billing/bill/${bill.id}`)
                        }
                      >
                        Edit Bill
                      </button>
                      {bill.receipt_pdf_url ? (
                        <button
                          style={{
                            ...actionBtn('default'),
                            backgroundColor: '#f5f3ff', color: '#7c3aed',
                            border: '1px solid #ddd6fe',
                          }}
                          onClick={() => setReceiptPreviewBill(bill)}
                        >
                          View/Edit Receipt
                        </button>
                      ) : (
                        <button
                          style={{
                            ...actionBtn('default'),
                            backgroundColor: '#f0fdf4', color: '#15803d',
                            border: '1px solid #bbf7d0',
                          }}
                          onClick={() => navigate(`/billing/receipt/${bill.id}`)}
                        >
                          Create Payment Receipt
                        </button>
                      )}
                      <button
                        style={actionBtn('red')}
                        onClick={() => handleDelete(
                          bill.id,
                          bill.invoice_no
                            ? `Invoice ${bill.invoice_no}`
                            : `Bill #${bill.bill_no}`,
                        )}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Bill type picker modal */}
      {showTypePicker && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)',
            zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowTypePicker(false)}
        >
          <div
            style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '2rem',
              width: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
              Select Bill Type
            </h3>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#64748b' }}>
              Choose the type of invoice to create for {patient?.patient_name}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {BILL_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    setShowTypePicker(false);
                    if (t.key === 'consumables') {
                      navigate(`/billing/consumables/new/${patient!.id}`);
                    } else if (t.key === 'consumable_return') {
                      navigate(`/billing/consumables/return/new/${patient!.id}`);
                    } else if (t.key === 'equipment_rental') {
                      navigate(`/billing/equipment-rental/new/${patient!.id}`, { state: { billType: 'equipment_rental' } });
                    } else if (t.key === 'equipment_sale') {
                      navigate(`/billing/equipment-sale/new/${patient!.id}`, { state: { billType: 'equipment_sale' } });
                    } else {
                      navigate(`/billing/new/${patient!.id}`, { state: { billType: t.key } });
                    }
                  }}
                  style={{
                    padding: '1rem', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: t.bg, border: `1.5px solid ${t.border}`,
                    borderRadius: '10px', transition: 'transform 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: t.color }}>{t.label}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{t.desc}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowTypePicker(false)}
              style={{ marginTop: '1.25rem', width: '100%', padding: '0.6rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', color: '#64748b', fontSize: '0.85rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bill View Modal (read-only PDF preview) */}
      {viewBillId !== null && patient && (
        <BillViewModal
          billId={viewBillId}
          patient={patient}
          onClose={() => setViewBillId(null)}
        />
      )}

      {/* Payment Receipt Preview Modal */}
      {receiptPreviewBill && patient && (
        <PaymentReceiptPreview
          bill={receiptPreviewBill}
          patient={patient}
          onClose={() => setReceiptPreviewBill(null)}
        />
      )}

      {/* Fixed-position status dropdown — escapes overflow:hidden */}
      {statusDropdownOpen !== null && (
        <div
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: '140px',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_OPTIONS.map((s) => {
            const currentBill = bills.find((b) => b.id === statusDropdownOpen);
            const isActive = currentBill?.status === s;
            return (
              <div
                key={s}
                style={{
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: '#334155',
                  fontWeight: isActive ? 700 : 400,
                  backgroundColor: isActive ? '#f1f5f9' : 'white',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isActive ? '#f1f5f9' : 'white')}
                onClick={() => handleStatusChange(statusDropdownOpen, s)}
              >
                {s}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatientBillingHistory;
